import * as XLSX from 'xlsx';
import { excelBase64 } from '../data/excelData';

/**
 * Base64 string'den Excel dosyasını okur ve parse eder
 * @returns {Promise<Object>} Parse edilmiş Excel verisi
 */
export const readExcelFromBase64 = async () => {
  try {
    console.log('Excel dosyası base64 string\'den okunuyor...');
    
    // Base64 string'i ArrayBuffer'a çevir
    const binaryString = atob(excelBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const arrayBuffer = bytes.buffer;
    
    console.log('Excel dosyası base64\'den decode edildi, boyut:', arrayBuffer.byteLength, 'bytes');
    
    // Excel dosyasını parse et
    const workbook = XLSX.read(arrayBuffer, { 
      type: 'array',
      cellDates: false,
      cellNF: false,
    });
    
    console.log('Excel workbook okundu, sheet sayısı:', workbook.SheetNames.length);
    console.log('Sheet isimleri:', workbook.SheetNames);
    
    const result = {};
    
    // Her sheet'i parse et
    workbook.SheetNames.forEach((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      console.log(`Sheet "${sheetName}": ${range.e.r + 1} satır, ${range.e.c + 1} kolon`);
      
      const rawData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1,
        defval: '',
        raw: false,
        blankrows: true,
      });
      
      console.log(`Sheet "${sheetName}" parse edildi, ${rawData.length} satır`);
      result[sheetName] = rawData;
    });
    
    return result;
  } catch (error) {
    console.error('Excel dosyası base64\'den okunurken hata:', error);
    console.error('Hata detayları:', error.message);
    console.error('Stack:', error.stack);
    throw error;
  }
};

/**
 * Excel dosyasını okur ve parse eder (fallback - fetch ile)
 * @param {string} filePath - Excel dosyasının yolu (public klasöründen)
 * @returns {Promise<Object>} Parse edilmiş Excel verisi
 */
export const readExcelFile = async (filePath) => {
  try {
    console.log('📂 Excel dosyası okunuyor:', filePath);
    console.log('🌐 Base URL:', window.location.origin);
    console.log('🌐 Pathname:', window.location.pathname);
    console.log('🌐 Full URL:', window.location.href);
    console.log('🌐 PUBLIC_URL:', process.env.PUBLIC_URL || '(yok)');
    
    // Production'da dosya yolunu düzelt
    // React'te public klasöründeki dosyalar root'tan erişilebilir
    let fullPath = filePath;
    let url;
    
    // Eğer baseUrl ile başlıyorsa, direkt kullan
    if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
      url = filePath;
    } else {
      // Relative path'i normalize et
      if (!filePath.startsWith('/')) {
        fullPath = '/' + filePath;
      }
      
      // PUBLIC_URL varsa kullan (React build'de genelde boş string)
      const publicUrl = process.env.PUBLIC_URL || '';
      
      // Production'da subdirectory varsa (örn: /app), path'e ekle
      const basePath = window.location.pathname.split('/').slice(0, -1).join('/') || '';
      
      // Önce PUBLIC_URL ile dene, sonra basePath ile
      if (publicUrl) {
        url = window.location.origin + publicUrl + fullPath;
      } else if (basePath) {
        url = window.location.origin + basePath + fullPath;
      } else {
        url = window.location.origin + fullPath;
      }
    }
    
    console.log('🔗 Fetch URL:', url);
    console.log('📁 Full Path:', fullPath);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*',
      },
      cache: 'no-cache',
      credentials: 'same-origin'
    });
    
    console.log('📡 Response Status:', response.status, response.statusText);
    console.log('📡 Response OK:', response.ok);
    console.log('📡 Response Type:', response.type);
    
    // Content-Type kontrolü
    const contentType = response.headers.get('content-type') || '';
    console.log('📡 Content-Type:', contentType);
    
    if (!response.ok) {
      console.error('❌ HTTP Error:', response.status, response.statusText);
      console.error('📄 Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Response body'yi de oku (hata mesajı için)
      try {
        const errorText = await response.text();
        console.error('📄 Response body:', errorText.substring(0, 500));
      } catch (e) {
        console.error('📄 Response body okunamadı');
      }
      
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const arrayBuffer = await response.arrayBuffer();
    console.log('✅ Dosya indirildi, boyut:', arrayBuffer.byteLength, 'bytes');
    
    if (arrayBuffer.byteLength === 0) {
      throw new Error('Dosya boş!');
    }
    
    // İlk birkaç byte'ı kontrol et (Excel dosyası signature'ı)
    const signature = new Uint8Array(arrayBuffer.slice(0, 4));
    const isExcelFile = signature[0] === 0x50 && signature[1] === 0x4B; // PK (ZIP signature)
    
    // HTML kontrolü - eğer HTML döndürülüyorsa hata ver
    const textDecoder = new TextDecoder();
    const firstBytes = textDecoder.decode(arrayBuffer.slice(0, 100));
    const isHTML = firstBytes.trim().toLowerCase().startsWith('<!doctype') || 
                   firstBytes.trim().toLowerCase().startsWith('<html') ||
                   contentType.includes('text/html');
    
    if (isHTML) {
      console.error('❌ Server HTML döndürüyor (Excel dosyası bulunamadı)');
      console.error('📄 İlk 200 karakter:', firstBytes.substring(0, 200));
      throw new Error(`Server Excel dosyası yerine HTML döndürdü. Dosya bulunamadı veya yanlış yolda. URL: ${url}`);
    }
    
    if (!isExcelFile && !contentType.includes('excel') && !contentType.includes('spreadsheet') && !contentType.includes('octet-stream')) {
      console.warn('⚠️ Dosya signature Excel dosyası değil');
      console.warn('📋 İlk 50 byte (hex):', Array.from(signature).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '));
      console.warn('📋 İlk 100 karakter:', textDecoder.decode(arrayBuffer.slice(0, 100)));
    }
    
    console.log('📋 Dosya signature kontrolü:', isExcelFile ? '✅ Excel dosyası' : '⚠️ Excel dosyası olmayabilir');
    
    const workbook = XLSX.read(arrayBuffer, { 
      type: 'array',
      cellDates: false, // Tarihleri string olarak oku
      cellNF: false, // Number format'ı koruma
    });
    
    console.log('📊 Excel workbook okundu, sheet sayısı:', workbook.SheetNames.length);
    console.log('📋 Sheet isimleri:', workbook.SheetNames);
    
    const result = {};
    
    // Her sheet'i parse et
    workbook.SheetNames.forEach((sheetName) => {
      const worksheet = workbook.Sheets[sheetName];
      
      // Sheet'in boyutunu kontrol et
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      console.log(`📄 Sheet "${sheetName}": ${range.e.r + 1} satır, ${range.e.c + 1} kolon`);
      
      // Önce header'ı bulmak için ilk birkaç satırı oku
      const rawData = XLSX.utils.sheet_to_json(worksheet, { 
        header: 1, // Array formatında
        defval: '', // Boş hücreler için varsayılan değer
        raw: false, // String olarak oku
        blankrows: true, // Boş satırları da dahil et
      });
      
      console.log(`✅ Sheet "${sheetName}" parse edildi, ${rawData.length} satır`);
      
      result[sheetName] = rawData;
    });
    
    return result;
  } catch (error) {
    console.error('❌ Excel dosyası okunurken hata:', error);
    console.error('Hata detayları:', error.message);
    console.error('Stack:', error.stack);
    throw error;
  }
};

/**
 * Excel verisini analiz eder ve header satırını bulur
 * @param {Array} sheetData - Excel sheet verisi
 * @returns {Object} { headerRowIndex, headers, dataRows }
 */
export const analyzeExcelStructure = (sheetData) => {
  if (!sheetData || sheetData.length === 0) {
    return { headerRowIndex: -1, headers: [], dataRows: [] };
  }
  
  console.log('🔍 Excel yapısı analiz ediliyor...');
  console.log('📊 Toplam satır sayısı:', sheetData.length);
  console.log('📋 İlk 10 satır:', sheetData.slice(0, 10));
  
  // Tüm kolonları analiz et
  console.log('📊 Kolon Analizi:');
  const maxCols = Math.max(...sheetData.slice(0, 20).map(row => row ? row.length : 0));
  console.log(`  - Maksimum kolon sayısı (ilk 20 satır): ${maxCols}`);
  
  // İlk 10 satırdaki her kolonu göster
  for (let col = 0; col < Math.min(maxCols, 15); col++) {
    const colValues = sheetData.slice(0, 10).map(row => row && row[col] ? String(row[col]).trim() : '').filter(v => v);
    if (colValues.length > 0) {
      console.log(`  - Kolon ${col}: ${colValues.slice(0, 5).join(' | ')}${colValues.length > 5 ? '...' : ''}`);
    }
  }
  
  // Header satırını bul
  // Excel dosyalarında genelde:
  // - İlk satır: Başlık (ör: "PRESTİJ PARKLAR BÖLGESİ")
  // - İkinci satır: Gerçek header (ör: "SIRA NO", "İL", "İLÇE", "MAHALLE", "BÖLGE", "PARK", vb.)
  // - Üçüncü satır ve sonrası: Veri satırları
  
  let headerRowIndex = -1;
  const headerKeywords = ['il', 'ilçe', 'ilce', 'mahalle', 'bölge', 'bolge', 'park', 'alan', 'çim', 'cim', 'tüketim', 'tuketim', 'personel', 'saha'];
  const realHeaderIndicators = ['sira no', 'sıra no', 'sira', 'no', 'il', 'ilçe', 'ilce', 'mahalle', 'bölge', 'bolge', 'park', 'adres'];
  
  // İlk 10 satırı kontrol et (header genelde üstte olur)
  for (let i = 0; i < Math.min(10, sheetData.length); i++) {
    const row = sheetData[i];
    if (!row || !Array.isArray(row)) {
      if (i < 5) console.log(`  Satır ${i}: Geçersiz satır`);
      continue;
    }
    
    // Satırın ilk 15 kolonunu kontrol et
    const rowText = row.slice(0, 15).map(cell => String(cell || '').toLowerCase().trim()).join(' ');
    const rowFirstCells = row.slice(0, 5).map(cell => String(cell || '').toLowerCase().trim());
    
    // Gerçek header göstergelerini kontrol et (SIRA NO, İL, İLÇE gibi)
    const hasRealHeaderIndicator = realHeaderIndicators.some(indicator => 
      rowFirstCells.some(cell => cell.includes(indicator))
    );
    
    // Header keyword'lerini kontrol et
    const matches = headerKeywords.filter(keyword => rowText.includes(keyword));
    
    if (i < 5) {
      console.log(`  Satır ${i}: ${matches.length} keyword eşleşme, ${hasRealHeaderIndicator ? 'gerçek header göstergesi VAR' : 'gerçek header göstergesi YOK'} - İlk 5 kolon: [${row.slice(0, 5).map(c => `"${String(c || '').substring(0, 20)}"`).join(', ')}]`);
    }
    
    // Gerçek header göstergesi varsa veya en az 2 keyword eşleşmesi varsa header olarak kabul et
    if (hasRealHeaderIndicator || matches.length >= 2) {
      headerRowIndex = i;
      console.log(`✅ Header satırı bulundu: ${i} (${hasRealHeaderIndicator ? 'gerçek header göstergesi' : `${matches.length} keyword eşleşme: ${matches.join(', ')}`})`);
      break;
    }
  }
  
  // Header bulunamadıysa ilk satırı header olarak kullan
  if (headerRowIndex === -1) {
    console.warn('⚠️ Header satırı bulunamadı, ilk satır kullanılıyor');
    console.log('📋 İlk satır:', sheetData[0]?.slice(0, 10));
    headerRowIndex = 0;
  }
  
  const headers = sheetData[headerRowIndex] || [];
  console.log('📋 Header satırı:', headers);
  
  const dataRows = sheetData.slice(headerRowIndex + 1).filter(row => {
    // Boş satırları filtrele - en az 2 hücre dolu olmalı
    if (!row || !Array.isArray(row)) return false;
    const filledCells = row.filter(cell => cell !== null && cell !== undefined && String(cell).trim() !== '').length;
    return filledCells >= 2; // En az 2 dolu hücre olmalı
  });
  
  console.log(`📊 ${dataRows.length} veri satırı bulundu`);
  
  return { headerRowIndex, headers, dataRows };
};

/**
 * Header kolonlarını eşleştirir (farklı isimlendirmeleri destekler)
 * @param {Array} headers - Header satırı
 * @returns {Object} Kolon indeksleri
 */
export const mapColumnIndices = (headers) => {
  const indices = {
    il: -1,
    ilce: -1,
    mahalle: -1,
    bolge: -1,
    park: -1,
    alan: -1,
    cimAlan: -1,
    sonAyTuketim: -1,
    durum: -1,
    sahaPersoneliIsim: -1,
    sahaPersoneliTelefon: -1,
    sahaPersoneliEmail: -1,
    adres: -1,
    sertZemin: -1,
    ioa: -1,
    ciceklik: -1,
    yurumeYollari: -1,
    pasif: -1,
  };
  
  console.log('🔍 Kolon eşleştirmesi yapılıyor...');
  console.log('📋 Header kolonları:', headers);
  
  headers.forEach((header, index) => {
    const headerLower = String(header || '').toLowerCase().trim();
    
    // İl (sadece "il" kelimesi, "ilçe" değil)
    if (headerLower === 'il' || (headerLower.includes('il') && !headerLower.includes('ilçe') && !headerLower.includes('ilce') && indices.il === -1)) {
      indices.il = index;
      console.log(`  ✅ İl kolonu bulundu: ${index} - "${header}"`);
    }
    // İlçe
    if (headerLower.includes('ilçe') || headerLower.includes('ilce') || headerLower === 'ilçe') {
      indices.ilce = index;
      console.log(`  ✅ İlçe kolonu bulundu: ${index} - "${header}"`);
    }
    // Mahalle
    if (headerLower.includes('mahalle') || headerLower === 'mahalle') {
      indices.mahalle = index;
      console.log(`  ✅ Mahalle kolonu bulundu: ${index} - "${header}"`);
    }
    // Bölge
    if (headerLower.includes('bölge') || headerLower.includes('bolge') || headerLower === 'bölge') {
      indices.bolge = index;
      console.log(`  ✅ Bölge kolonu bulundu: ${index} - "${header}"`);
    }
    // Park
    if (headerLower.includes('park') || headerLower === 'park') {
      indices.park = index;
      console.log(`  ✅ Park kolonu bulundu: ${index} - "${header}"`);
    }
    // Alan (toplam alan, genel alan)
    if ((headerLower.includes('alan') || headerLower.includes('area')) && !headerLower.includes('çim') && !headerLower.includes('cim') && indices.alan === -1) {
      indices.alan = index;
      console.log(`  ✅ Alan kolonu bulundu: ${index} - "${header}"`);
    }
    // Çim Alanı
    if (headerLower.includes('çim') || headerLower.includes('cim') || headerLower.includes('grass')) {
      indices.cimAlan = index;
      console.log(`  ✅ Çim Alanı kolonu bulundu: ${index} - "${header}"`);
    }
    // Tüketim
    if (headerLower.includes('tüketim') || headerLower.includes('tuketim') || headerLower.includes('consumption') || headerLower.includes('su')) {
      indices.sonAyTuketim = index;
      console.log(`  ✅ Tüketim kolonu bulundu: ${index} - "${header}"`);
    }
    // Durum
    if (headerLower.includes('durum') || headerLower.includes('status') || headerLower.includes('state')) {
      indices.durum = index;
      console.log(`  ✅ Durum kolonu bulundu: ${index} - "${header}"`);
    }
    // Saha Personeli - İsim
    if ((headerLower.includes('personel') || headerLower.includes('saha')) && (headerLower.includes('isim') || headerLower.includes('ad') || headerLower.includes('name'))) {
      indices.sahaPersoneliIsim = index;
      console.log(`  ✅ Saha Personeli İsim kolonu bulundu: ${index} - "${header}"`);
    }
    // Saha Personeli - Telefon
    if ((headerLower.includes('personel') || headerLower.includes('saha')) && (headerLower.includes('telefon') || headerLower.includes('phone') || headerLower.includes('tel'))) {
      indices.sahaPersoneliTelefon = index;
      console.log(`  ✅ Saha Personeli Telefon kolonu bulundu: ${index} - "${header}"`);
    }
    // Saha Personeli - Email
    if ((headerLower.includes('personel') || headerLower.includes('saha')) && (headerLower.includes('email') || headerLower.includes('e-posta') || headerLower.includes('eposta') || headerLower.includes('mail'))) {
      indices.sahaPersoneliEmail = index;
      console.log(`  ✅ Saha Personeli Email kolonu bulundu: ${index} - "${header}"`);
    }
    // Adres
    if (headerLower.includes('adres') || headerLower.includes('address') || headerLower === 'adres') {
      indices.adres = index;
      console.log(`  ✅ Adres kolonu bulundu: ${index} - "${header}"`);
    }
    // Sert Zemin
    if (headerLower.includes('sert') && headerLower.includes('zemin')) {
      indices.sertZemin = index;
      console.log(`  ✅ Sert Zemin kolonu bulundu: ${index} - "${header}"`);
    }
    // İ.O.A
    if (headerLower.includes('i.o.a') || headerLower.includes('ioa') || headerLower === 'i.o.a') {
      indices.ioa = index;
      console.log(`  ✅ İ.O.A kolonu bulundu: ${index} - "${header}"`);
    }
    // Çiçeklik
    if (headerLower.includes('çiçek') || headerLower.includes('cicek') || headerLower.includes('flower')) {
      indices.ciceklik = index;
      console.log(`  ✅ Çiçeklik kolonu bulundu: ${index} - "${header}"`);
    }
    // Yürüme Yolları
    if (headerLower.includes('yürüme') || headerLower.includes('yurume') || (headerLower.includes('yol') && headerLower.includes('yürü'))) {
      indices.yurumeYollari = index;
      console.log(`  ✅ Yürüme Yolları kolonu bulundu: ${index} - "${header}"`);
    }
    // Pasif
    if (headerLower.includes('pasif') || headerLower === 'pasif') {
      indices.pasif = index;
      console.log(`  ✅ Pasif kolonu bulundu: ${index} - "${header}"`);
    }
  });
  
  console.log('📊 Kolon eşleştirme sonucu:', indices);
  
  return indices;
};

/**
 * Excel verisini yapılandırılmış formata dönüştürür
 * @param {Array} excelData - Excel'den parse edilmiş ham veri
 * @returns {Array} Yapılandırılmış veri
 */
export const parseRegionData = (excelData) => {
  if (!excelData || excelData.length === 0) {
    return [];
  }
  
  // İlk satır header olmalı
  const headers = excelData[0];
  const rows = excelData.slice(1);
  
  return rows.map((row) => {
    const item = {};
    headers.forEach((header, index) => {
      if (header) {
        item[header] = row[index] || '';
      }
    });
    return item;
  });
};

/**
 * Excel sheet'ini parse eder ve yapılandırılmış veri döner
 * @param {Array} sheetData - Excel sheet verisi (array of arrays)
 * @param {string} sheetName - Sheet adı (PARK, METRAJ, vb.)
 * @returns {Array} Parse edilmiş park verileri
 */
export const parseExcelSheet = (sheetData, sheetName = '') => {
  if (!sheetData || sheetData.length === 0) {
    console.warn('⚠️ Sheet verisi boş veya geçersiz');
    return [];
  }
  
  console.log(`📊 parseExcelSheet başlatılıyor (Sheet: ${sheetName})...`);
  console.log('📋 Sheet verisi uzunluğu:', sheetData.length);
  console.log('📋 İlk 10 satır:', sheetData.slice(0, 10));
  
  // PARK sheet'i için özel parse mantığı
  if (sheetName === 'PARK') {
    return parseParkSheet(sheetData);
  }
  
  // Diğer sheet'ler için genel parse
  // Excel yapısını analiz et
  const { headers, dataRows } = analyzeExcelStructure(sheetData);
  
  console.log('📊 analyzeExcelStructure sonucu:');
  console.log('  - Header satırı:', headers);
  console.log('  - Header uzunluğu:', headers.length);
  console.log('  - Veri satırı sayısı:', dataRows.length);
  
  if (headers.length === 0) {
    console.error('❌ Excel dosyasında header bulunamadı');
    console.log('📋 İlk 10 satır:', sheetData.slice(0, 10));
    return [];
  }
  
  if (dataRows.length === 0) {
    console.warn('⚠️ Excel dosyasında veri satırı bulunamadı');
    console.log('📋 Header sonrası ilk 10 satır:', sheetData.slice(headers.length, headers.length + 10));
    return [];
  }
  
  // Kolon indekslerini eşleştir
  const columnIndices = mapColumnIndices(headers);
  
  console.log('📊 Excel Kolon Eşleştirmesi:', columnIndices);
  console.log('📋 Header Satırı:', headers);
  console.log('📈 Toplam Veri Satırı:', dataRows.length);
  
  // Veri satırlarını parse et
  const parsedData = [];
  let skippedRows = 0;
  
  dataRows.forEach((row, rowIndex) => {
    if (!row || !Array.isArray(row)) {
      skippedRows++;
      return;
    }
    
    // İl, İlçe, Mahalle, Bölge bilgilerini al
    // Eğer kolon indeksleri -1 ise, satırdaki tüm kolonları tarayarak bulmaya çalış
    let il = columnIndices.il >= 0 ? String(row[columnIndices.il] || '').trim() : '';
    let ilce = columnIndices.ilce >= 0 ? String(row[columnIndices.ilce] || '').trim() : '';
    let mahalle = columnIndices.mahalle >= 0 ? String(row[columnIndices.mahalle] || '').trim() : '';
    let bolge = columnIndices.bolge >= 0 ? String(row[columnIndices.bolge] || '').trim() : '';
    
    // Sayısal değerleri bölge olarak kabul etme (SIRA NO gibi)
    if (bolge && /^\d+$/.test(bolge.trim())) {
      console.log(`  ⚠️ Satır ${rowIndex + 1}: Sayısal değer "${bolge}" bölge olarak kabul edilmedi`);
      bolge = '';
    }
    let park = columnIndices.park >= 0 ? String(row[columnIndices.park] || '').trim() : '';
    
    // Park adı yoksa, satırdaki diğer kolonlardan park adını bulmaya çalış (ÖNCE PARK ADINI BUL)
    let parkName = park;
    if (!parkName) {
      // Park kolonu bulunamadıysa, ilk birkaç dolu kolonu kontrol et
      for (let i = 0; i < Math.min(10, row.length); i++) {
        const cell = String(row[i] || '').trim();
        // Park adı gibi görünen hücreyi bul (uzunluk > 3, sayı değil)
        if (cell && cell.length > 3 && isNaN(parseFloat(cell)) && 
            !cell.toLowerCase().includes('aktif') && 
            !cell.toLowerCase().includes('bakım') &&
            !cell.toLowerCase().includes('il') &&
            !cell.toLowerCase().includes('ilçe') &&
            !cell.toLowerCase().includes('sira') &&
            !cell.toLowerCase().includes('no')) {
          parkName = cell;
          console.log(`  ℹ️ Satır ${rowIndex + 1}: Park adı "${parkName}" kolon ${i}'den alındı`);
          break;
        }
      }
    }
    
    // Eğer kolonlar bulunamadıysa, satırdaki tüm kolonları tarayarak bulmaya çalış
    if (columnIndices.il === -1 || columnIndices.ilce === -1) {
      // Tüm kolonları tarayarak il ve ilçe bilgilerini bul
      for (let i = 0; i < Math.min(20, row.length); i++) {
        const cell = String(row[i] || '').trim();
        const cellLower = cell.toLowerCase();
        
        // İl bulunamadıysa
        if (!il && (cellLower.includes('ankara') || cellLower.includes('istanbul') || cellLower.includes('izmir'))) {
          il = cell;
        }
        
        // İlçe bulunamadıysa
        if (!ilce && (cellLower.includes('çankaya') || cellLower.includes('etimesgut') || cellLower.includes('keçiören'))) {
          ilce = cell;
        }
        
        // Mahalle bulunamadıysa
        if (!mahalle && (cellLower.includes('mahalle') || cellLower.length > 5 && !isNaN(parseFloat(cell)) === false)) {
          // Mahalle isimleri genelde uzun ve sayı değil
          if (cell.length > 5 && isNaN(parseFloat(cell))) {
            mahalle = cell;
          }
        }
        
        // Bölge bulunamadıysa (sayısal değerleri bölge olarak kabul etme)
        if (!bolge && cellLower.includes('bölge') && !/^\d+$/.test(cell.trim())) {
          bolge = cell;
        }
        
        // Park bulunamadıysa
        if (!park && cellLower.includes('park') && cell.length > 5) {
          park = cell;
        }
      }
    }
    
    // Debug: İlk birkaç satırı göster
    if (rowIndex < 5) {
      console.log(`📄 Satır ${rowIndex + 1}:`, {
        il,
        ilce,
        mahalle,
        bolge,
        park,
        parkName,
        rawRow: row.slice(0, 10), // İlk 10 kolonu göster
      });
    }
    
    // İl ve İlçe bilgisi olmalı, ancak bazı Excel dosyalarında bu bilgiler farklı kolonlarda olabilir
    // Eğer il ve ilçe yoksa, satırdaki diğer kolonlardan bulmaya çalış
    let ilValue = il;
    let ilceValue = ilce;
    
    // İl bulunamadıysa, satırdaki diğer kolonlardan "Ankara", "İstanbul" gibi il isimlerini ara
    if (!ilValue) {
      const ilNames = ['ankara', 'istanbul', 'izmir', 'antalya', 'bursa', 'adana', 'gaziantep', 'konya', 'kayseri', 'mersin'];
      for (let i = 0; i < Math.min(15, row.length); i++) {
        const cell = String(row[i] || '').toLowerCase().trim();
        if (ilNames.some(ilName => cell.includes(ilName))) {
          ilValue = String(row[i] || '').trim();
          console.log(`  ℹ️ Satır ${rowIndex + 1}: İl "${ilValue}" kolon ${i}'den alındı`);
          break;
        }
      }
    }
    
    // İlçe bulunamadıysa, satırdaki diğer kolonlardan ilçe isimlerini ara
    if (!ilceValue) {
      const ilceNames = ['çankaya', 'etimesgut', 'keçiören', 'yenimahalle', 'mamak', 'sincan', 'altındağ', 'pursaklar'];
      for (let i = 0; i < Math.min(15, row.length); i++) {
        const cell = String(row[i] || '').toLowerCase().trim();
        if (ilceNames.some(ilceName => cell.includes(ilceName))) {
          ilceValue = String(row[i] || '').trim();
          console.log(`  ℹ️ Satır ${rowIndex + 1}: İlçe "${ilceValue}" kolon ${i}'den alındı`);
          break;
        }
      }
    }
    
    // Hala il veya ilçe yoksa, satırı atla (ama park adı varsa ve il/ilçe bilgisi başka satırlardan geliyorsa, varsayılan değer kullan)
    if (!ilValue && !ilceValue) {
      // Eğer park adı varsa ve bu Çankaya belediyesi için ise, varsayılan değerler kullan
      if (parkName) {
        // Çankaya belediyesi için varsayılan değerler
        ilValue = 'Ankara';
        ilceValue = 'Çankaya';
        console.log(`  ℹ️ Satır ${rowIndex + 1}: İl ve İlçe bulunamadı, varsayılan değerler kullanılıyor (Ankara, Çankaya)`);
      } else {
        skippedRows++;
        if (rowIndex < 10) {
          console.log(`  ⚠️ Satır ${rowIndex + 1} atlandı - Eksik bilgi: il="${il}", ilce="${ilce}", park="${parkName}"`);
        }
        return; // Bu satırı atla
      }
    }
    
    // Hala park adı yoksa, satırı atla
    if (!parkName) {
      skippedRows++;
      if (rowIndex < 10) {
        console.log(`  ⚠️ Satır ${rowIndex + 1} atlandı - Park adı bulunamadı`);
      }
      return;
    }
    
    // Sayısal değerleri parse et
    const alan = columnIndices.alan >= 0 ? parseFloat(String(row[columnIndices.alan] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const cimAlan = columnIndices.cimAlan >= 0 ? parseFloat(String(row[columnIndices.cimAlan] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const sonAyTuketim = columnIndices.sonAyTuketim >= 0 ? parseFloat(String(row[columnIndices.sonAyTuketim] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const durum = columnIndices.durum >= 0 ? String(row[columnIndices.durum] || 'Aktif').trim() : 'Aktif';
    
    // Ekstra alanlar
    const adres = columnIndices.adres >= 0 ? String(row[columnIndices.adres] || '').trim() : '';
    const sertZemin = columnIndices.sertZemin >= 0 ? parseFloat(String(row[columnIndices.sertZemin] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const ioa = columnIndices.ioa >= 0 ? parseFloat(String(row[columnIndices.ioa] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const ciceklik = columnIndices.ciceklik >= 0 ? parseFloat(String(row[columnIndices.ciceklik] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const yurumeYollari = columnIndices.yurumeYollari >= 0 ? parseFloat(String(row[columnIndices.yurumeYollari] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    const pasif = columnIndices.pasif >= 0 ? parseFloat(String(row[columnIndices.pasif] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0 : 0;
    
    // Saha personeli bilgileri
    const sahaPersoneliIsim = columnIndices.sahaPersoneliIsim >= 0 ? String(row[columnIndices.sahaPersoneliIsim] || '').trim() : '';
    const sahaPersoneliTelefon = columnIndices.sahaPersoneliTelefon >= 0 ? String(row[columnIndices.sahaPersoneliTelefon] || '').trim() : '';
    const sahaPersoneliEmail = columnIndices.sahaPersoneliEmail >= 0 ? String(row[columnIndices.sahaPersoneliEmail] || '').trim() : '';
    
    // Tüm kolonları sakla (ekstra veriler için)
    const allColumns = {};
    headers.forEach((header, idx) => {
      if (header && String(header).trim()) {
        const headerKey = String(header).trim().toLowerCase().replace(/\s+/g, '_');
        allColumns[headerKey] = String(row[idx] || '').trim();
      }
    });
    
    parsedData.push({
      il: ilValue || 'Ankara', // Varsayılan olarak Ankara
      ilce: ilceValue || 'Çankaya', // Varsayılan olarak Çankaya
      mahalle: mahalle || 'Belirtilmemiş',
      bolge: bolge || 'Belirtilmemiş',
      park: parkName,
      alan,
      cimAlan,
      sonAyTuketim,
      durum,
      adres,
      sertZemin,
      ioa,
      ciceklik,
      yurumeYollari,
      pasif,
      sahaPersoneli: {
        isim: sahaPersoneliIsim,
        telefon: sahaPersoneliTelefon,
        email: sahaPersoneliEmail,
      },
      // Tüm Excel kolonlarını sakla
      allColumns: allColumns,
      rawRow: row, // Debug için ham veri
      rowIndex: rowIndex + 1, // Excel satır numarası (1-based)
      // Excel'den gelen gerçek veri, örnek değil
      isExample: false,
    });
    
    // İlk 10 başarılı parse'i göster
    if (parsedData.length <= 10) {
      console.log(`  ✅ Satır ${rowIndex + 1} parse edildi:`, {
        il,
        ilce,
        mahalle: mahalle || 'Belirtilmemiş',
        bolge: bolge || 'Belirtilmemiş',
        park: parkName,
      });
    }
  });
  
  console.log(`✅ ${parsedData.length} park verisi parse edildi`);
  console.log(`⚠️ ${skippedRows} satır atlandı (eksik bilgi veya boş satır)`);
  
  if (parsedData.length === 0) {
    console.error('❌ Hiç veri parse edilemedi! Excel yapısını kontrol edin.');
    console.log('📋 İlk 10 veri satırı:', dataRows.slice(0, 10));
    console.log('📋 Header kolonları:', headers);
    console.log('📊 Kolon eşleştirmesi:', columnIndices);
    
    // Tüm kolonları göster
    console.log('📋 Tüm kolonlar:');
    headers.forEach((header, idx) => {
      console.log(`  [${idx}] ${header || '(boş)'}`);
    });
  } else {
    // Parse edilen verilerin özeti
    const ilSet = new Set(parsedData.map(d => d.il));
    const ilceSet = new Set(parsedData.map(d => `${d.il}-${d.ilce}`));
    const parkSet = new Set(parsedData.map(d => d.park));
    
    console.log('📊 Parse edilen verilerin özeti:');
    console.log(`  - Farklı İl: ${ilSet.size}`);
    console.log(`  - Farklı İlçe: ${ilceSet.size}`);
    console.log(`  - Farklı Park: ${parkSet.size}`);
    console.log(`  - İller: ${Array.from(ilSet).join(', ')}`);
  }
  
  return parsedData;
};

/**
 * PARK sheet'ini özel olarak parse eder
 * PARK sheet yapısı:
 * - Satır 0-1: Boş
 * - Satır 2: Bölge başlığı (örn: "PRESTİJ PARKLAR BÖLGESİ")
 * - Satır 3: Bölge mühendisi/formen bilgisi
 * - Satır 4: Header: ['SIRA NO', 'PARK ADI', 'ADRES', 'MAHALLE', 'ÇİM ALAN', 'SERT ZEMİN', 'İ.O.A', 'ÇİÇEKLİK', 'YÜRÜME YOLLARI', ...]
 * - Satır 5: Alt header (KAUÇUK, SUNİ ÇİM, BETON, ÇAKIL)
 * - Satır 6+: Veri satırları
 */
const parseParkSheet = (sheetData) => {
  const parsedData = [];
  let currentBolge = '';
  let currentBolgeInfo = '';
  
  // İl ve İlçe sabit (Excel'de sadece Çankaya var)
  const il = 'Ankara';
  const ilce = 'Çankaya';
  
  console.log('🏞️ PARK sheet parse ediliyor...');
  
  for (let i = 0; i < sheetData.length; i++) {
    const row = sheetData[i];
    if (!row || !Array.isArray(row)) continue;
    
    const firstCell = String(row[0] || '').trim();
    const secondCell = String(row[1] || '').trim();
    
    // Bölge başlığı satırı (örn: "PRESTİJ PARKLAR BÖLGESİ")
    if (firstCell && (firstCell.includes('BÖLGE') || firstCell.includes('BOLGE')) && !firstCell.includes('SIRA')) {
      currentBolge = firstCell.replace('BÖLGESİ', '').replace('BOLGESI', '').replace('BÖLGE', '').replace('BOLGE', '').trim();
      // Sayısal değerleri bölge olarak kabul etme
      if (currentBolge && /^\d+$/.test(currentBolge.trim())) {
        console.log(`  ⚠️ Satır ${i + 1}: Sayısal bölge "${currentBolge}" geçersiz, atlanıyor`);
        currentBolge = '';
        continue;
      }
      console.log(`  📍 Bölge bulundu: ${currentBolge}`);
      continue;
    }
    
    // Bölge mühendisi/formen bilgisi satırı
    if (firstCell && (firstCell.includes('MÜH:') || firstCell.includes('MUH:') || firstCell.includes('FORMEN'))) {
      currentBolgeInfo = firstCell;
      continue;
    }
    
    // Header satırı (SIRA NO ile başlayan)
    if (firstCell && (firstCell.includes('SIRA') || firstCell.includes('SIRA NO'))) {
      console.log(`  📋 Header satırı bulundu (satır ${i}):`, row.slice(0, 10));
      continue;
    }
    
    // Alt header satırı (KAUÇUK, SUNİ ÇİM gibi) - atla
    if (!firstCell && (secondCell.includes('KAUÇUK') || secondCell.includes('SUNİ') || secondCell.includes('BETON'))) {
      continue;
    }
    
    // Veri satırı kontrolü
    // SIRA NO kolonu sayı ise veya PARK ADI kolonu dolu ise veri satırıdır
    const siraNo = firstCell ? parseInt(firstCell) : null;
    const parkAdi = secondCell || '';
    
    if (siraNo || (parkAdi && parkAdi.length > 2 && !parkAdi.includes('SIRA') && !parkAdi.includes('NO'))) {
      // Veri satırı parse et
      const mahalle = String(row[3] || '').trim();
      const cimAlan = parseFloat(String(row[4] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const sertZemin = parseFloat(String(row[5] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const ioa = parseFloat(String(row[6] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const ciceklik = parseFloat(String(row[7] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const yurumeYollari = parseFloat(String(row[8] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const pasif = parseFloat(String(row[9] || '0').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
      const adres = String(row[2] || '').trim();
      
      // Toplam alan = Çim Alan + Sert Zemin + İ.O.A + Çiçeklik + Yürüme Yolları + Pasif
      const toplamAlan = cimAlan + sertZemin + ioa + ciceklik + yurumeYollari + pasif;
      
      // Bölge yoksa adres kolonundan çıkar veya "Genel" kullan
      // Sayısal değerleri bölge olarak kabul etme
      let bolge = currentBolge || '';
      
      // Eğer bölge yoksa, adres kolonundan çıkar (adresin ilk kısmı bölge olabilir)
      if (!bolge && adres && adres.trim() !== '') {
        // Adresin ilk kısmını bölge olarak dene (virgül veya boşluk ile ayrılmış)
        const adresParts = adres.split(/[,，]/);
        if (adresParts.length > 0) {
          const potentialBolge = adresParts[0].trim();
          // Sayısal değilse ve yeterince uzunsa bölge olarak kullan
          if (potentialBolge && !/^\d+$/.test(potentialBolge) && potentialBolge.length > 3) {
            bolge = potentialBolge;
            console.log(`  ℹ️ Satır ${i + 1}: Bölge adresten çıkarıldı: "${bolge}"`);
          }
        }
      }
      
      // Hala bölge yoksa "Genel" kullan
      if (!bolge || bolge.trim() === '') {
        bolge = 'Genel';
      }
      
      // Sayısal değerleri bölge olarak kabul etme
      if (bolge && /^\d+$/.test(bolge.trim())) {
        console.log(`  ⚠️ Satır ${i + 1}: Sayısal bölge "${bolge}" geçersiz, "Genel" kullanılıyor`);
        bolge = 'Genel';
      }
      
      if (parkAdi && parkAdi.length > 2) {
        parsedData.push({
          il,
          ilce,
          mahalle: mahalle || 'Belirtilmemiş',
          bolge: bolge,
          park: parkAdi,
          alan: toplamAlan,
          cimAlan: cimAlan,
          sertZemin: sertZemin,
          ioa: ioa,
          ciceklik: ciceklik,
          yurumeYollari: yurumeYollari,
          pasif: pasif,
          adres: adres,
          sonAyTuketim: 0, // Excel'de yok, varsayılan 0
          durum: 'Aktif',
          sahaPersoneli: {
            isim: currentBolgeInfo || '',
            telefon: '',
            email: '',
          },
          rawRow: row,
          rowIndex: i + 1,
          // Excel'den gelen gerçek veri, örnek değil
          isExample: false,
        });
        
        if (parsedData.length <= 10) {
          console.log(`  ✅ Satır ${i + 1} parse edildi: ${parkAdi} (${bolge}, ${mahalle})`);
        }
      }
    }
  }
  
  console.log(`✅ PARK sheet'inden ${parsedData.length} park verisi parse edildi`);
  
  return parsedData;
};

/**
 * Bölge verilerini hiyerarşik yapıya dönüştürür
 * @param {Array} regionData - Parse edilmiş bölge verileri (array of objects with il, ilce, mahalle, bolge, park properties)
 * @returns {Object} Hiyerarşik yapı: { il: { ilce: { mahalle: { bolge: [...] } } } }
 */
export const buildHierarchy = (regionData) => {
  const hierarchy = {};
  
  regionData.forEach((item) => {
    const il = item.il || '';
    const ilce = item.ilce || '';
    const mahalle = item.mahalle || '';
    const bolge = item.bolge || '';
    const park = item.park || '';
    
    // İl ve İlçe zorunlu, diğerleri opsiyonel
    if (!il || !ilce || !park) return;
    
    if (!hierarchy[il]) {
      hierarchy[il] = {};
    }
    if (!hierarchy[il][ilce]) {
      hierarchy[il][ilce] = {};
    }
    
    // Mahalle yoksa "Genel" olarak ekle
    const mahalleKey = mahalle || 'Genel';
    if (!hierarchy[il][ilce][mahalleKey]) {
      hierarchy[il][ilce][mahalleKey] = {};
    }
    
    // Bölge yoksa "Genel" olarak ekle
    // Sayısal değerleri bölge olarak kabul etme
    let bolgeKey = bolge || 'Genel';
    if (bolgeKey && /^\d+$/.test(bolgeKey.trim())) {
      console.log(`  ⚠️ Sayısal bölge "${bolgeKey}" geçersiz, "Genel" kullanılıyor`);
      bolgeKey = 'Genel';
    }
    
    if (!hierarchy[il][ilce][mahalleKey][bolgeKey]) {
      hierarchy[il][ilce][mahalleKey][bolgeKey] = [];
    }
    
    // Park bilgilerini ekle (tüm verileri dahil et)
    hierarchy[il][ilce][mahalleKey][bolgeKey].push({
      name: park,
      alan: parseFloat(item.alan) || 0,
      cimAlan: parseFloat(item.cimAlan) || 0,
      sonAyTuketim: parseFloat(item.sonAyTuketim) || 0,
      durum: item.durum || 'Aktif',
      sahaPersoneli: {
        isim: item.sahaPersoneli?.isim || '',
        telefon: item.sahaPersoneli?.telefon || '',
        email: item.sahaPersoneli?.email || '',
      },
      // Tüm ekstra verileri ekle
      allColumns: item.allColumns || {},
      rawRow: item.rawRow || [],
      rowIndex: item.rowIndex || 0,
      // Ekstra alanlar (varsa)
      sertZemin: parseFloat(item.sertZemin) || 0,
      ioa: parseFloat(item.ioa) || 0,
      ciceklik: parseFloat(item.ciceklik) || 0,
      yurumeYollari: parseFloat(item.yurumeYollari) || 0,
      pasif: parseFloat(item.pasif) || 0,
      adres: item.adres || '',
      // Örnek veri mi kontrolü (mock data'dan geliyorsa)
      isExample: item.isExample || false,
    });
  });
  
  return hierarchy;
};

